/**
 * ENG-9394: read Claude usage at HOST level, on a schedule, and post it.
 *
 * WHY A SECOND USAGE PRODUCER EXISTS ALONGSIDE `usage-banner-monitor.ts`.
 *
 * The banner monitor scrapes a status line out of a running agent's `pane.log`.
 * That signal is wrong in three ways that no amount of parsing fixes:
 *
 *   1. It is PER-AGENT and the quantity is PER-LOGIN. One host, one Claude
 *      login, a dozen agents - so one fact produced thirteen
 *      `usage_limit_reached` criticals on 2026-08-24.
 *   2. It cannot RE-READ. A banner is whatever last rendered in that agent's
 *      pane, so a reading taken once is presented indefinitely as current. On
 *      2026-08-24 those thirteen readings were between one and five hours old
 *      with nothing in the record saying so.
 *   3. The saturated weekly form renders only an hour, no date (ENG-7904), so
 *      the reset instant is a reconstruction that can never be more than 24h
 *      out. Nothing could auto-close against it.
 *
 * This poller has none of those. It asks the CLI directly, once per host, on a
 * fixed interval, and gets a dated answer it can ask for again at any time.
 *
 * WHY THIS IS NOT THE tmux DESIGN THE TICKET DESCRIBES. ENG-9394 specifies a
 * throw-away tmux session running the `/usage` TUI, because PR #1307 (ENG-5443,
 * May 2026) established that `claude -p "/usage"` does not expand slash
 * commands. That was true then. On CLI 2.1.241 it is false - print mode expands
 * it and returns the full report. Verified on a live host before this was
 * written, including the ticket's two open risks:
 *
 *   - Cost: `num_turns: 0`, `total_cost_usd: 0`, every token counter 0. No model
 *     is invoked; it is local metadata. Polling forever is free.
 *   - Latency: ~2.9s, consistently, across a 13-sample run.
 *
 * So there is no tmux session, no TUI, no pane scraping, no session churn on a
 * twelve-agent host, and no collision with `claude_auth_status` probing or the
 * ENG-5357 brew-upgrade throttle. It is a plain subprocess with a timeout.
 *
 * BECAUSE IT IS A CLI-VERSION FACT AND NOT A CONTRACT, every failure here
 * degrades to `unknown` and never to `at limit`. A CLI downgrade or a reworded
 * render must surface as a POLLER fault, not as a fleet-wide usage event - that
 * is the exact failure mode this whole ticket exists to remove, and reproducing
 * it in the replacement would be the worst possible outcome.
 */
import {
  buildHostUsageReading,
  parseUsageReport,
  stabiliseResetInstant,
  HOST_USAGE_POLL_INTERVAL_MS,
  type HostUsageReading,
} from '@augmented/core';

/**
 * Host-level poll cadence. Matches `RUNTIME_AUTH_PROBE_INTERVAL_MS`'s shape.
 *
 * DEFINED IN CORE, re-exported here. The server expresses its freshness and
 * clock-skew rules in multiples of this interval, so producer and consumer have
 * to agree on it; a second copy in this file is a number that can drift out of
 * step with the one the rules are actually applied with.
 */
export { HOST_USAGE_POLL_INTERVAL_MS };

/**
 * Hard ceiling on the subprocess. Measured cost is ~2.9s; 60s is generous enough
 * to absorb a loaded host and short enough that a wedged CLI cannot hold the
 * poll slot for a whole interval.
 */
export const HOST_USAGE_POLL_TIMEOUT_MS = 60_000;

/** The slash command we ask for, and the output mode that makes it machine-readable. */
export const USAGE_COMMAND_ARGS = ['-p', '/usage', '--output-format', 'json'] as const;

/**
 * Minimal API surface, injected so the unit test does not drag the real
 * transport in. Same shape as `UsageBannerMonitorApi`.
 */
export interface HostUsagePollerApi {
  post(path: string, body: unknown): Promise<unknown>;
}

/** Result of actually running the CLI. Injected so tests never spawn a process. */
export interface UsageCommandResult {
  /** Raw stdout. Expected to be the `--output-format json` envelope. */
  stdout: string;
  /** True when the command failed to run, timed out, or exited non-zero. */
  failed: boolean;
}

export interface HostUsagePollerDeps {
  api: HostUsagePollerApi;
  /** Runs `claude -p /usage --output-format json`. */
  runUsageCommand: () => Promise<UsageCommandResult>;
  /** The host's configured `claude_auth_mode`. */
  authMode: string | null | undefined;
  /** Claude Code version string, recorded with the reading. */
  cliVersion: string | null;
  log: (msg: string) => void;
  now?: Date;
}

interface PollerState {
  lastPolledAt: number;
  inFlight: boolean;
  /**
   * The last reset instants the SERVER told us it has stored, per window. Held
   * so `stabiliseResetInstant` can absorb the CLI's render jitter - see that
   * function for the measurement. Without this the stored reset walks by a
   * minute every few polls and every walk mints a new alert dedupe key.
   *
   * THIS IS MODULE MEMORY AND MODULE MEMORY DIES ON RESTART (Ada's review of
   * #4988). Initialised to null and never seeded, the first poll after every
   * manager restart had `previous === null`, took whatever the CLI happened to
   * render at that instant, and posted it - one spurious dedupe-key change per
   * restart, on a window that never moved. Manager restarts are not rare; every
   * integration change on an agent fires one.
   *
   * Two things fix that and both are needed. The DURABLE anchor is now the host
   * row itself: `POST /host/usage-readings` compares against what it already has
   * before writing, so a cold poller cannot move a stored instant however
   * confused its own memory is. And the response reports back what the server
   * actually stored, which is what re-seeds these fields - so after the first
   * poll of a fresh process they hold the real anchor rather than a guess.
   */
  lastWeekResetsAt: Date | null;
  lastSessionResetsAt: Date | null;
}

const state: PollerState = {
  lastPolledAt: 0,
  inFlight: false,
  lastWeekResetsAt: null,
  lastSessionResetsAt: null,
};

/** Test seam. */
export function _resetHostUsagePollerState(): void {
  state.lastPolledAt = 0;
  state.inFlight = false;
  state.lastWeekResetsAt = null;
  state.lastSessionResetsAt = null;
}

/**
 * Pull the `result` string out of the `--output-format json` envelope.
 *
 * Returns null rather than throwing on anything unexpected. A malformed envelope
 * is a poller fault and must land as `unknown`.
 */
export function extractUsageText(stdout: string): string | null {
  try {
    const parsed = JSON.parse(stdout) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const envelope = parsed as { result?: unknown; is_error?: unknown };
    if (envelope.is_error === true) return null;
    return typeof envelope.result === 'string' ? envelope.result : null;
  } catch {
    return null;
  }
}

/**
 * What the server reports it actually stored for each window.
 *
 * TRI-STATE ON PURPOSE. `undefined` means the server said nothing about that
 * window - an older API, or a response shape we do not recognise - and the
 * caller keeps what it sent. `null` means the server stored NO instant, which is
 * a real answer (an `unknown` reading clears both columns) and must be adopted,
 * not treated as silence. Collapsing the two with `??` would leave a stale
 * anchor in place after the row was cleared, and the next `ok` reading would be
 * stabilised against a window that no longer exists.
 */
export interface StoredResetInstants {
  week?: Date | null;
  session?: Date | null;
}

function readStoredInstant(raw: unknown): Date | null | undefined {
  if (raw === null) return null;
  if (typeof raw !== 'string' || raw === '') return undefined;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * Pull the server's authoritative reset instants out of the POST response.
 *
 * Never throws and never guesses: anything it cannot read is `undefined`, which
 * the caller reads as "keep what you had".
 */
export function storedResetInstants(response: unknown): StoredResetInstants {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return {};
  const body = response as Record<string, unknown>;
  const out: StoredResetInstants = {};
  const week = readStoredInstant(body['week_resets_at']);
  if (week !== undefined) out.week = week;
  const session = readStoredInstant(body['session_resets_at']);
  if (session !== undefined) out.session = session;
  return out;
}

/**
 * Poll host usage if the interval has elapsed, and POST the reading.
 *
 * Fire-and-forget from the poll cycle. Errors are logged, never thrown: an
 * observability path must not break the manager loop.
 */
export async function maybePollHostUsage(deps: HostUsagePollerDeps): Promise<void> {
  const { api, runUsageCommand, authMode, cliVersion, log } = deps;
  const now = deps.now ?? new Date();
  const nowMs = now.getTime();

  if (state.inFlight) return;
  if (nowMs - state.lastPolledAt < HOST_USAGE_POLL_INTERVAL_MS) return;

  state.inFlight = true;
  try {
    let reading: HostUsageReading;
    try {
      const { stdout, failed } = await runUsageCommand();
      const text = failed ? null : extractUsageText(stdout);
      reading = buildHostUsageReading({
        report: text === null ? null : parseUsageReport(text, now),
        readAt: now,
        cliVersion,
        authMode,
        pollFailed: failed,
      });
    } catch (err) {
      // A throw from the subprocess helper is a failed poll, not an absent
      // quota. `pollFailed` keeps it `unknown`.
      log(`[host-usage] /usage poll failed: ${(err as Error).message}`);
      reading = buildHostUsageReading({
        report: null,
        readAt: now,
        cliVersion,
        authMode,
        pollFailed: true,
      });
    }

    // Absorb the CLI's reset-render jitter before it reaches the server.
    const weekResetsAt = stabiliseResetInstant(state.lastWeekResetsAt, reading.weekResetsAt);
    const sessionResetsAt = stabiliseResetInstant(
      state.lastSessionResetsAt,
      reading.sessionResetsAt,
    );

    const response = await api.post('/host/usage-readings', {
      state: reading.state,
      week_pct: reading.weekPct,
      week_resets_at: weekResetsAt ? weekResetsAt.toISOString() : null,
      session_pct: reading.sessionPct,
      session_resets_at: sessionResetsAt ? sessionResetsAt.toISOString() : null,
      read_at: reading.readAt.toISOString(),
      cli_version: reading.cliVersion,
      source: reading.source,
      unknown_reason: reading.unknownReason,
    });

    // Re-anchor on what the server SAYS IT STORED, not on what we sent. On the
    // first poll of a fresh process these two differ by exactly the jitter this
    // whole mechanism exists to absorb: we posted 1am because module memory was
    // empty, the row still holds 12:59am, and the server kept 12:59am. Adopting
    // its answer is what stops the next poll re-litigating the same minute.
    // Falls back to what we sent if the response says nothing useful, which is
    // the pre-existing behaviour and no worse than it.
    const stored = storedResetInstants(response);
    state.lastWeekResetsAt = stored.week !== undefined ? stored.week : weekResetsAt;
    state.lastSessionResetsAt = stored.session !== undefined ? stored.session : sessionResetsAt;

    if (reading.state === 'ok') {
      const anchoredWeek = state.lastWeekResetsAt;
      log(
        `[host-usage] week ${reading.weekPct}%` +
          (anchoredWeek ? ` (resets ${anchoredWeek.toISOString()})` : '') +
          `, session ${reading.sessionPct}%`,
      );
    } else if (reading.state === 'unknown') {
      log(`[host-usage] reading unknown: ${reading.unknownReason}`);
    }
  } catch (err) {
    log(`[host-usage] post failed: ${(err as Error).message}`);
  } finally {
    // Stamp the attempt, not the success. A host whose CLI is wedged must not
    // retry every poll cycle; the reading simply expires and reads `unknown`.
    state.lastPolledAt = nowMs;
    state.inFlight = false;
  }
}
